import React, { useState, useEffect } from 'react';
import { FaSpinner, FaExclamationCircle, FaArrowUp, FaCheckCircle, FaHourglass, FaTimesCircle } from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';

const WithdrawalRecord = ({ userDetails, token }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Styles
  const styles = {
    container: {
      backgroundColor: '#0F172A',
      color: '#E2E8F0',
      borderRadius: '12px',
      padding: '16px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    header: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#F8FAFC',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    summary: {
      backgroundColor: '#1E293B',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    summaryItem: {
      textAlign: 'center',
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#94A3B8',
      marginBottom: '4px',
    },
    summaryValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#F8FAFC',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    card: {
      backgroundColor: '#1E293B',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #334155',
      transition: 'transform 0.2s ease',
    },
    cardHeader: {
      backgroundColor: 'rgba(15, 23, 42, 0.5)',
      padding: '12px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    amountContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    withdrawalIcon: {
      color: '#EF4444',
      fontSize: '18px',
    },
    amountInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    amount: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#F8FAFC',
      display: 'flex',
      alignItems: 'center',
    },
    currency: {
      fontSize: '13px',
      color: '#94A3B8',
      marginRight: '4px',
    },
    date: {
      fontSize: '12px',
      color: '#94A3B8',
    },
    statusBadge: {
      padding: '6px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    approvedStatus: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
    },
    pendingStatus: {
      backgroundColor: 'rgba(234, 179, 8, 0.1)',
      color: '#EAB308',
    },
    rejectedStatus: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
    },
    cardBody: {
      padding: '16px',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '10px',
    },
    detailLabel: {
      fontSize: '13px',
      color: '#94A3B8',
    },
    detailValue: {
      fontSize: '13px',
      color: '#F8FAFC',
      fontWeight: '500',
    },
    message: {
      marginTop: '12px',
      padding: '10px 12px',
      borderRadius: '8px',
      fontSize: '13px',
    },
    messageApproved: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
    },
    messagePending: {
      backgroundColor: 'rgba(234, 179, 8, 0.1)',
      color: '#EAB308',
    },
    messageRejected: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 0',
      color: '#94A3B8',
    },
    loaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 0',
    },
    spinner: {
      fontSize: '24px',
      color: '#38BDF8',
      animation: 'spin 1s linear infinite',
      marginBottom: '12px',
    },
    loaderText: {
      fontSize: '14px',
      color: '#94A3B8',
    },
    errorContainer: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderRadius: '8px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    errorIcon: {
      fontSize: '20px',
      color: '#EF4444',
    },
    errorText: {
      fontSize: '14px',
      color: '#F8FAFC',
    },
  };

  useEffect(() => {
    const fetchWithdrawals = async () => {
      try {
        const response = await axios.get(
          `https://api.globallequityfinance.com/api/withdrawals/history/${userDetails.email}`,
          { headers: { 'Authorization': token } }
        );
        setWithdrawals(response.data);
      } catch (err) {
        setError('Failed to load withdrawal records');
        console.error('Error fetching withdrawals:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWithdrawals();
  }, [userDetails.email, token]);

  const getStatusIcon = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <FaCheckCircle />;
      case 'PENDING':
        return <FaHourglass />;
      case 'REJECTED':
        return <FaTimesCircle />;
      default:
        return null;
    }
  };

  const getStatusStyle = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return styles.approvedStatus;
      case 'PENDING':
        return styles.pendingStatus;
      case 'REJECTED':
        return styles.rejectedStatus;
      default:
        return {};
    }
  };

  const getMessageStyle = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return styles.messageApproved;
      case 'PENDING':
        return styles.messagePending;
      case 'REJECTED':
        return styles.messageRejected;
      default:
        return {};
    }
  };

  const getStatusMessage = (status) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'Congratulations! Your withdrawal has been processed successfully.';
      case 'PENDING':
        return 'Your withdrawal request is being processed. This usually takes 1-3 business days.';
      case 'REJECTED':
        return 'Your withdrawal request was declined. Please contact support for more information.';
      default:
        return '';
    }
  };

  // Calculate total withdrawn amount
  const getTotalWithdrawn = () => {
    return withdrawals
      .filter(w => w.withdrawalStatus.toUpperCase() === 'APPROVED')
      .reduce((sum, w) => sum + w.amountToReceive, 0);
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loaderContainer}>
          <FaSpinner style={styles.spinner} />
          <p style={styles.loaderText}>Loading withdrawal history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <FaExclamationCircle style={styles.errorIcon} />
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {withdrawals.length > 0 && (
        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Total Withdrawals</div>
            <div style={styles.summaryValue}>{withdrawals.length}</div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Total Withdrawn</div>
            <div style={styles.summaryValue}>KES {getTotalWithdrawn().toLocaleString()}</div>
          </div>
        </div>
      )}
      
      <div style={styles.content}>
        {withdrawals.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No withdrawal records found</p>
          </div>
        ) : (
          withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.amountContainer}>
                  <FaArrowUp style={styles.withdrawalIcon} />
                  <div style={styles.amountInfo}>
                    <div style={styles.amount}>
                      <span style={styles.currency}>KES</span>
                      {withdrawal.requestedAmount.toLocaleString()}
                    </div>
                    <div style={styles.date}>
                      {format(new Date(withdrawal.requestTime), 'MMM d, yyyy • h:mm a')}
                    </div>
                  </div>
                </div>
                <div 
                  style={{
                    ...styles.statusBadge,
                    ...getStatusStyle(withdrawal.withdrawalStatus)
                  }}
                >
                  {getStatusIcon(withdrawal.withdrawalStatus)} {withdrawal.withdrawalStatus}
                </div>
              </div>
              
              <div style={styles.cardBody}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Order Number</span>
                  <span style={styles.detailValue}>#{withdrawal.orderNumber}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Amount to Receive</span>
                  <span style={styles.detailValue}>KES {withdrawal.amountToReceive.toLocaleString()}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Processing Fee</span>
                  <span style={styles.detailValue}>
                    KES {(withdrawal.requestedAmount - withdrawal.amountToReceive).toLocaleString()}
                  </span>
                </div>
                <div 
                  style={{
                    ...styles.message,
                    ...getMessageStyle(withdrawal.withdrawalStatus)
                  }}
                >
                  {getStatusMessage(withdrawal.withdrawalStatus)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WithdrawalRecord;